import { AuthError, Session, User } from '@supabase/supabase-js';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AUTH_RETURN_TO_KEY } from '../constants/adminSettings';
import { supabaseClient } from '../services/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithProvider: (provider: 'google') => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: any) => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<{ error: AuthError | null }>;
  isProfileComplete: () => boolean;
  trackDatabaseDownload: (databaseName: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session }, error } = await supabaseClient.auth.getSession();
      if (error) {
        console.error('Error getting session:', error);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      const { error } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabaseClient.auth.signInWithPassword({
        email,
        password
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signInWithProvider = async (provider: 'google') => {
    try {
      // Save current path so we can return after OAuth (don't send users back to landing)
      const returnTo = typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : '';
      if (returnTo && returnTo !== '/' && !returnTo.startsWith('/auth')) {
        try {
          sessionStorage.setItem(AUTH_RETURN_TO_KEY, returnTo);
        } catch (_e) { /* ignore */ }
      }
      const { error } = await supabaseClient.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabaseClient.auth.signOut();
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
        redirectTo: `https://climateplans.org/auth/reset-password`
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const updateProfile = async (updates: any) => {
    try {
      const { error } = await supabaseClient.auth.updateUser({
        data: updates
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const refreshSession = async () => {
    try {
      const { error } = await supabaseClient.auth.refreshSession();
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const isProfileComplete = () => {
    if (!user?.user_metadata) return false;
    
    const metadata = user.user_metadata;
    return !!(
      metadata.first_name?.trim() &&
      metadata.last_name?.trim() &&
      metadata.organization?.trim() &&
      metadata.user_type &&
      metadata.location_type &&
      metadata.specific_location &&
      metadata.use_cases &&
      Array.isArray(metadata.use_cases) &&
      metadata.use_cases.length > 0 &&
      (!metadata.use_cases.includes('Other') || metadata.other_use_case?.trim()) &&
      metadata.accept_terms
    );
  };

  const trackDatabaseDownload = async (databaseName: string) => {
    if (!user?.user_metadata) return { error: new Error('User not authenticated') };
    
    try {
      const currentDownloads = user.user_metadata.downloaded_databases || [];
      const downloadsSet = new Set([...currentDownloads, databaseName]);
      const updatedDownloads = Array.from(downloadsSet); // Remove duplicates
      
      const { error } = await supabaseClient.auth.updateUser({
        data: {
          ...user.user_metadata,
          downloaded_databases: updatedDownloads
        }
      });
      
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithProvider,
    signOut,
    resetPassword,
    updateProfile,
    refreshSession,
    isProfileComplete,
    trackDatabaseDownload
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
